import {DetailPanel, DetailKvTable} from "../../../components/detailLayout";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {ContentStatus} from "../../../constant/CodeSpecNum";
import {useDispatch, useSelector} from "react-redux";
import Th_title from "../../../components/Th_title";
import ReadOnlyInputBasic from "../../../components/input/ReadOnlyInputBasic";
import {ButtonBasic} from "../../../components/buttons/ButtonBasic";
import TextAreaBasic from "../../../components/input/TextAreaBasic";
import {changeRefreshContentStatus} from "../../../redux/slice/statusSlice";
import {handleActiveError, handleActiveToast} from "../../../components/sonner/ToastBasic";
import {
    changeNextContentStatus,
    changePreviousContentStatus,
    createReworkRequest,
} from "../../../api/contentStatus/contentStatusService";
import {reviewConfirm, reviewSave} from "../../../api/process/ProcessService";
import {changeStatusNumericValue} from "../../../redux/slice/contentSlice";
import {convertDate, handleValidation} from "../../serviceRequest/serviceRequest/serviceUtils";
import {useTranslation} from "react-i18next";
import SimmsDatePicker from "../../../components/datepicker/SimmsDatePicker";
import {dateOnChange} from "../../../components/commonmethod/simmsDateMethod";
import dayjs from "dayjs";
import {
    getMaintenanceVerificationConfig,
    resolveVerificationContentId,
    resolveVerificationEndDateField,
} from "./maintenanceVerificationConfig";
import {
    TaskActivityCommentRow,
    TaskActivityCycleCard,
    TaskActivityCycleCompose,
    TaskActivityWorkCompose,
    TaskActivityWorkSection,
} from "./TaskProgressThread";
import {resolveThreadUser, resolveThreadUserName} from "./taskProgressThreadUtils";
import {formatWorkCloseDateDisplay, buildReview} from "./workProcessUtils";

function buildVerificationState(data, config, reviewContext) {
    const hideEndDateAdjustment = config.hideEndDateAdjustment ?? false;
    const endDateField = hideEndDateAdjustment ? null : resolveVerificationEndDateField(data, config);
    const state = {
        id: resolveVerificationContentId(data, config),
        verificationCloseDate: data.verificationCloseDate,
        verificationDescription: reviewContext?.initialOpinion ?? data.verificationDescription,
    };

    if (endDateField) {
        state[endDateField] = data[endDateField];
    }

    if (config.includeRequestCloseDates) {
        state.requestCloseDate = data.requestCloseDate;
        state.adjustedRequestCloseDate = data.adjustedRequestCloseDate;
    }

    return state;
}

function VerificationSectionHeader() {
    return (
        <tr className="task-progress-panel__section-head">
            <Th_title title="검수" className="fieldset-td" colspan={3}>
                <span className="task-progress-panel__section-desc">위 작업내역을 검수합니다.</span>
            </Th_title>
        </tr>
    );
}

function MaintenanceVerification({
    data,
    variant = "inspection",
    embedded = false,
    embeddedLayout = "table",
    suppressCompletedInFeed = false,
    feedWorkContext = null,
    feedAttachToPriorWork = false,
    reviewContext = null,
}) {
    const config = useMemo(() => getMaintenanceVerificationConfig(variant), [variant]);
    const useReviewApi = Boolean(reviewContext?.documentId && reviewContext?.reworkId);
    const reviewType = reviewContext?.contentCategory ?? config.contentCategory;
    const {
        contentCategory,
        linkedContentCategory,
        reworkType,
        endDateLabel,
        useTime,
        updateVerification,
        completeVerification,
        completeOrder = "statusFirst",
        showVerifierInReadOnly,
        hideRework = false,
        hideEndDateAdjustment = false,
    } = config;

    const endDateField = hideEndDateAdjustment ? null : resolveVerificationEndDateField(data, config);
    const contentId = resolveVerificationContentId(data, config);
    const endDateConstraint = config.getEndDateConstraint?.(data) ?? data[endDateField];

    const [verification, setVerification] = useState(() => buildVerificationState(data, config, reviewContext));
    const [contentSaved, setContentSaved] = useState(() => (
        useReviewApi
            ? Boolean(reviewContext?.opinionSaved)
            : Boolean(data.verifiedUserId)
    ));
    const [isEditing, setIsEditing] = useState(() => (
        useReviewApi
            ? !reviewContext?.opinionSaved
            : !data.verifiedUserId
    ));
    const [editSnapshot, setEditSnapshot] = useState("");
    const [reworkDeadlineOpen, setReworkDeadlineOpen] = useState(false);
    const textareaWrapRef = useRef(null);
    const shouldFocusTextareaRef = useRef(false);

    const {t} = useTranslation();
    const refreshContent = useSelector((state) => state.status.refreshContent);
    const statusNumericValue = useSelector((state) => state.content.statusNumericValue);
    const currentUser = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();

    const isSavedOnServer = contentSaved || (!useReviewApi && Boolean(data.verifiedUserId));

    const buildReviewPayload = (pass) => buildReview({
        type: reviewType,
        opinion: verification.verificationDescription,
        ...(pass !== undefined ? {pass} : {}),
        ...(pass === false ? {newDeadline: verification[endDateField]} : {}),
    });

    const refreshAfterReview = async () => {
        dispatch(changeRefreshContentStatus(!refreshContent));
        await reviewContext?.onRefresh?.();
    };

    const save = async () => {
        if (!verification.verificationDescription?.trim()) {
            window.alert("검수의견을 입력해주세요.");
            return;
        }

        if (!window.confirm("저장하시겠습니까?")) return;

        const wasSaved = isSavedOnServer;

        if (useReviewApi) {
            const response = await reviewSave(reviewContext.reworkId, buildReviewPayload());
            if (response && response.status === 200) {
                await refreshAfterReview();
                setContentSaved(true);
                setIsEditing(false);
                setEditSnapshot("");
                handleActiveToast({
                    message: wasSaved ? "정보가 수정되었습니다." : "정보가 등록되었습니다.",
                });
            } else {
                handleActiveError({message: "서버에러"});
            }
            return;
        }

        const payload = {id: contentId, ...verification, modification: true};
        const response = await updateVerification(payload);
        if (response && response.status === 200) {
            dispatch(changeRefreshContentStatus(!refreshContent));
            setContentSaved(true);
            setIsEditing(false);
            setEditSnapshot("");
            handleActiveToast({
                message: wasSaved ? "정보가 수정되었습니다." : "정보가 등록되었습니다.",
            });
        } else {
            handleActiveError({message: "서버에러"});
        }
    };

    const startEdit = () => {
        const current = verification.verificationDescription ?? data.verificationDescription ?? "";
        setEditSnapshot(current);
        shouldFocusTextareaRef.current = true;
        setIsEditing(true);
    };

    useEffect(() => {
        if (!isEditing || !shouldFocusTextareaRef.current) return;

        const textarea = textareaWrapRef.current?.querySelector("textarea");
        if (!textarea) return;

        shouldFocusTextareaRef.current = false;
        requestAnimationFrame(() => {
            textarea.focus();
            const end = textarea.value.length;
            textarea.setSelectionRange(end, end);
        });
    }, [isEditing]);

    const cancelEdit = () => {
        setVerification((prev) => ({
            ...prev,
            verificationDescription: editSnapshot,
        }));
        setIsEditing(false);
    };

    const hasDescription = Boolean(verification.verificationDescription?.trim());
    const canComplete = isSavedOnServer && hasDescription && !isEditing;
    const canRework = hasDescription && !isEditing;
    const showTextarea = !isSavedOnServer || isEditing;
    const useFeedComposeLayout = embedded && embeddedLayout === "feed";

    const handleChangeNextStatus = async () => {
        if (!isSavedOnServer) {
            window.alert("검수의견을 저장한 후 검수완료할 수 있습니다.");
            return;
        }
        if (!handleValidation(verification.verificationDescription, "검수의견을 저장해주세요.")) return;

        if (!window.confirm(t("changeNextStep"))) return;

        try {
            if (useReviewApi) {
                await handleReviewConfirm(true);
            } else if (completeOrder === "verificationFirst") {
                await handleVerificationSave();
                await handleNextStepStatusChange();
            } else {
                await handleNextStepStatusChange();
                await handleVerificationSave();
            }
            handleActiveToast({message: t("verifiedComplete")});
        } catch (error) {
            handleActiveError(error);
        }
    };

    const handleNextStepStatusChange = async () => {
        const payload = {
            contentId,
            contentCategory,
            ...(linkedContentCategory ? {linkedContentCategory} : {}),
        };
        const response = await changeNextContentStatus(payload);

        if (!response || response.status !== 200) {
            throw {message: t("error"), description: t("contentStatus")};
        }

        dispatch(changeStatusNumericValue(0));
    };

    const handleVerificationSave = async () => {
        const saveData = {
            id: contentId,
            ...verification,
            modification: true,
            ...(completeVerification ? {} : {verificationCloseDate: convertDate()}),
        };

        const saveFn = completeVerification ?? updateVerification;
        const verificationResponse = await saveFn(saveData);

        if (!verificationResponse || verificationResponse.status !== 200) {
            throw {message: t("error")};
        }
        setVerification(buildVerificationState(data, config, reviewContext));
        dispatch(changeRefreshContentStatus(!refreshContent));
    };

    const handleReviewConfirm = async (pass) => {
        const response = await reviewConfirm(
            reviewContext.documentId,
            reviewContext.reworkId,
            buildReviewPayload(pass),
        );

        if (!response || response.status !== 200) {
            throw {message: t("error")};
        }

        dispatch(changeStatusNumericValue(0));
        await refreshAfterReview();
        setVerification(buildVerificationState(data, config, reviewContext));
    };

    const handleChangePreviousStatus = async () => {
        if (!handleValidation(verification.verificationDescription, "검수의견을 작성해주세요.")) return;

        if (!window.confirm(t("reworkStep"))) return;

        try {
            if (useReviewApi) {
                await handleReviewConfirm(false);
            } else {
                await handlePreviousStepStatusChange();
                await handleReworkRequestSave();
            }
            setReworkDeadlineOpen(false);
            handleActiveToast({message: t("reworkRequest")});
        } catch (error) {
            handleActiveError(error);
        }
    };

    const handleReworkButtonClick = () => {
        if (!canRework) return;

        if (useFeedComposeLayout) {
            setReworkDeadlineOpen(true);
            return;
        }

        handleChangePreviousStatus();
    };

    const showReworkDeadlineActions = useFeedComposeLayout && reworkDeadlineOpen;

    const cancelReworkDeadline = () => {
        setReworkDeadlineOpen(false);
        setVerification((prev) => ({
            ...prev,
            [endDateField]: data[endDateField],
            ...(config.includeRequestCloseDates
                ? {
                    requestCloseDate: data.requestCloseDate,
                    adjustedRequestCloseDate: data.adjustedRequestCloseDate,
                }
                : {}),
        }));
    };

    const currentEndDateDisplay = formatWorkCloseDateDisplay(data[endDateField]);

    const handlePreviousStepStatusChange = async () => {
        const payload = {
            contentId,
            contentCategory,
            ...(linkedContentCategory ? {linkedContentCategory} : {}),
        };
        const response = await changePreviousContentStatus(payload);

        if (!response || response.status !== 200) {
            throw {message: t("error"), description: t("contentStatus")};
        }

        dispatch(changeStatusNumericValue(0));
    };

    const handleReworkRequestSave = async () => {
        const reworkDates = config.getReworkCloseDates
            ? config.getReworkCloseDates(data, verification)
            : {
                beforeCloseDate: data[endDateField],
                afterCloseDate: verification[endDateField],
            };

        const saveData = {
            id: contentId,
            ...verification,
            requestDescription: verification.verificationDescription,
            requestCloseDate: convertDate(),
            ...reworkDates,
            type: reworkType,
        };

        const verificationResponse = await createReworkRequest(saveData);

        if (!verificationResponse || verificationResponse.status !== 200) {
            throw {message: t("error")};
        }
        setVerification(buildVerificationState(data, config, reviewContext));
        dispatch(changeRefreshContentStatus(!refreshContent));
    };

    useEffect(() => {
        setVerification(buildVerificationState(data, config, reviewContext));
        const saved = useReviewApi
            ? Boolean(reviewContext?.opinionSaved)
            : Boolean(data.verifiedUserId);
        setContentSaved(saved);
        setIsEditing(!saved);
        setEditSnapshot("");
        setReworkDeadlineOpen(false);
    }, [
        data.id,
        data.serviceRequestId,
        data.verificationCloseDate,
        data.verificationDescription,
        data.requestCloseDate,
        data.adjustedRequestCloseDate,
        ...(endDateField ? [data[endDateField]] : []),
        data.verifiedUserId,
        endDateField,
        variant,
        reviewContext?.documentId,
        reviewContext?.reworkId,
        reviewContext?.initialOpinion,
        reviewContext?.opinionSaved,
        useReviewApi,
    ]);

    const showComponent = () => {
        return data.verifiedUserId
            || statusNumericValue === ContentStatus.VERIFICATION.statusNumericValue;
    };

    const showVerificationInput = () => {
        if (config.getAuthUpdate) {
            return config.getAuthUpdate(data);
        }
        return data.auth?.update;
    };

    const verificationActionButtons = (
        <div className={`detail-kv-actions${useFeedComposeLayout ? " task-activity-compose__actions" : ""}`}>
            {!embedded && (
                <span
                    className={`detail-kv-actions__hint${
                        canComplete || (!hideRework && canRework) || isEditing ? " is-empty" : ""
                    }`}
                    aria-hidden={canComplete || (!hideRework && canRework) || isEditing}
                >
                    검수의견을 입력해주세요.
                </span>
            )}
            <div className="detail-kv-actions__buttons">
                {showReworkDeadlineActions ? (
                    <>
                        <ButtonBasic
                            text="취소"
                            className="detail-list-summary-action is-muted"
                            size="none"
                            onClick={cancelReworkDeadline}
                        />
                        <ButtonBasic
                            text="확정"
                            className="detail-list-summary-action detail-kv-actions__btn-rework"
                            size="none"
                            disabled={!canRework}
                            onClick={handleChangePreviousStatus}
                        />
                    </>
                ) : (
                    <>
                {!isSavedOnServer && (
                    <>
                        <ButtonBasic
                            text="저장"
                            className="detail-list-summary-action is-muted"
                            size="none"
                            onClick={save}
                        />
                        {!hideRework && (
                            <ButtonBasic
                                text="재작업요청"
                                className="detail-list-summary-action detail-kv-actions__btn-rework"
                                size="none"
                                disabled={!canRework}
                                onClick={handleReworkButtonClick}
                            />
                        )}
                        <ButtonBasic
                            text="검수완료"
                            className="detail-list-summary-action"
                            size="none"
                            disabled={!canComplete}
                            onClick={handleChangeNextStatus}
                        />
                    </>
                )}
                {isSavedOnServer && !isEditing && (
                    <>
                        <ButtonBasic
                            text="수정"
                            className="detail-list-summary-action is-muted"
                            size="none"
                            onClick={startEdit}
                        />
                        {!hideRework && (
                            <ButtonBasic
                                text="재작업요청"
                                className="detail-list-summary-action detail-kv-actions__btn-rework"
                                size="none"
                                disabled={!canRework}
                                onClick={handleReworkButtonClick}
                            />
                        )}
                        <ButtonBasic
                            text="검수완료"
                            className="detail-list-summary-action"
                            size="none"
                            disabled={!canComplete}
                            onClick={handleChangeNextStatus}
                        />
                    </>
                )}
                {isSavedOnServer && isEditing && (
                    <>
                        <ButtonBasic
                            text="저장"
                            className="detail-list-summary-action is-muted"
                            size="none"
                            onClick={save}
                        />
                        <ButtonBasic
                            text="취소"
                            className="detail-list-summary-action is-muted"
                            size="none"
                            onClick={cancelEdit}
                        />
                    </>
                )}
                    </>
                )}
            </div>
        </div>
    );

    const readOnlyRows = (
        <>
            {!embedded && (
                <tr>
                    <Th_title title={"검수완료일"} className="fieldset-td" colspan={3}>
                        <ReadOnlyInputBasic target={"verificationCloseDate"} value={data.verificationCloseDate} />
                    </Th_title>
                </tr>
            )}
            {showVerifierInReadOnly && !embedded && (
                <tr>
                    <Th_title title={"검수자"} className="fieldset-td" colspan={3}>
                        <ReadOnlyInputBasic
                            target={"verificationCloseUser"}
                            value={data.verificationCloseUser?.name}
                        />
                    </Th_title>
                </tr>
            )}
            <tr className="asset-detail-kv-row--auto">
                <Th_title title="검수의견" className="fieldset-td" colspan={3}>
                    <div className={embeddedLayout === "feed" ? "task-activity-text" : "align-text-div"}>
                        {data.verificationDescription}
                    </div>
                </Th_title>
            </tr>
        </>
    );

    const editRows = (
        <>
            <tr className="asset-detail-kv-row--auto">
                <Th_title title="검수의견" className="fieldset-td" colspan={3} required>
                    <div className="detail-kv-field-stack">
                        <div className="detail-kv-field-stack__input" ref={textareaWrapRef}>
                            {showTextarea ? (
                                <TextAreaBasic
                                    target="verificationDescription"
                                    placeholder="검수의견을 작성해주세요."
                                    data={{data: verification, setData: setVerification}}
                                />
                            ) : (
                                <div className={embeddedLayout === "feed" ? "task-activity-text" : "align-text-div"}>
                                    {verification.verificationDescription}
                                </div>
                            )}
                        </div>
                        {verificationActionButtons}
                    </div>
                </Th_title>
            </tr>
            {embeddedLayout !== "feed" && !hideEndDateAdjustment && endDateField && (
                <tr>
                    <Th_title title={endDateLabel} colspan={3} className="fieldset-td fieldset-td-input">
                        <div className="web-flex">
                            <SimmsDatePicker
                                onChange={(v) => dateOnChange(setVerification, endDateField, v, true)}
                                data={verification[endDateField]}
                                constraint={{
                                    largerThan: endDateConstraint && dayjs(endDateConstraint),
                                    required: false,
                                }}
                                time={useTime}
                                auth
                            />
                        </div>
                    </Th_title>
                </tr>
            )}
        </>
    );

    const verificationAuthor = resolveThreadUserName(data.verificationCloseUser) || currentUser?.name;
    const verificationUser = resolveThreadUser(data.verificationCloseUser) || currentUser;

    const verificationFeedCompose = (
        <div className="task-activity-compose">
            <div className="task-activity-compose__input" ref={textareaWrapRef}>
                {showTextarea ? (
                    <TextAreaBasic
                        target="verificationDescription"
                        placeholder="검수의견을 작성해주세요."
                        data={{data: verification, setData: setVerification}}
                    />
                ) : (
                    <div className="task-activity-text">{verification.verificationDescription}</div>
                )}
            </div>
            {reworkDeadlineOpen && !hideRework && !hideEndDateAdjustment && endDateField && (
                <div className="task-activity-compose__rework-deadline">
                    <p className="task-activity-compose__rework-deadline-head">
                        <span className="task-activity-compose__rework-deadline-label">{endDateLabel}</span>
                        {currentEndDateDisplay && (
                            <span className="task-activity-compose__rework-deadline-current">
                                현재
                                {" "}
                                <span className="task-activity-compose__rework-deadline-current-value">
                                    {currentEndDateDisplay}
                                </span>
                            </span>
                        )}
                    </p>
                    <SimmsDatePicker
                        onChange={(v) => dateOnChange(setVerification, endDateField, v, true)}
                        data={verification[endDateField]}
                        constraint={{
                            largerThan: endDateConstraint && dayjs(endDateConstraint),
                            required: false,
                        }}
                        time={useTime}
                        auth
                    />
                    <p className="task-activity-compose__rework-deadline-hint">
                        변경할 마감일을 선택한 뒤 확정을 눌러주세요.
                    </p>
                </div>
            )}
            {verificationActionButtons}
        </div>
    );

    const verificationFeedSection = (
        <TaskActivityWorkCompose
            user={verificationUser}
            name={verificationAuthor || undefined}
            metaLabel="검수"
            showAvatar
        >
            {verificationFeedCompose}
        </TaskActivityWorkCompose>
    );

    const showReadOnlyVerification =
        data.verificationCloseDate && showComponent()
        && statusNumericValue > ContentStatus.VERIFICATION.statusNumericValue;

    const showEditVerification =
        !data.verificationCloseDate && showVerificationInput()
        && statusNumericValue === ContentStatus.VERIFICATION.statusNumericValue;

    if (embedded) {
        if (embeddedLayout === "feed") {
            if (showReadOnlyVerification && !suppressCompletedInFeed) {
                return (
                    <TaskActivityCycleCard
                        user={data.verificationCloseUser || verificationUser}
                        name={data.verificationCloseUser?.name || verificationAuthor}
                        time={data.verificationCloseDate}
                        verification={{
                            user: data.verificationCloseUser || verificationUser,
                            name: data.verificationCloseUser?.name || verificationAuthor,
                            passed: true,
                        }}
                        verificationBody={data.verificationDescription?.trim() || "검수 내용 없음"}
                    />
                );
            }
            if (showEditVerification) {
                if (feedAttachToPriorWork) {
                    return (
                        <div className="task-activity-cycle__comments">
                            <TaskActivityCommentRow
                                user={verificationUser}
                                name={verificationAuthor || undefined}
                                label="검수"
                            >
                                <div className="task-activity-comment__compose">
                                    {verificationFeedCompose}
                                </div>
                            </TaskActivityCommentRow>
                        </div>
                    );
                }

                if (feedWorkContext?.workBody) {
                    return (
                        <TaskActivityCycleCompose
                            workSection={(
                                <TaskActivityWorkSection
                                    name={feedWorkContext.name}
                                    time={feedWorkContext.time}
                                    workDeadLine={feedWorkContext.workDeadLine}
                                    fileTable={feedWorkContext.fileTable}
                                    files={feedWorkContext.files}
                                >
                                    <div className="task-activity-work__body">{feedWorkContext.workBody}</div>
                                </TaskActivityWorkSection>
                            )}
                            verificationSection={verificationFeedSection}
                        />
                    );
                }

                return (
                    <TaskActivityCycleCompose workSection={verificationFeedSection} />
                );
            }
            return null;
        }

        if (showReadOnlyVerification) {
            return (
                <>
                    <VerificationSectionHeader />
                    {readOnlyRows}
                </>
            );
        }
        if (showEditVerification) {
            return (
                <>
                    <VerificationSectionHeader />
                    {editRows}
                </>
            );
        }
        return null;
    }

    return (
        <>
            {showReadOnlyVerification && (
                    <DetailPanel title={"검수관리"}>
                        <DetailKvTable>
                            <tbody>{readOnlyRows}</tbody>
                        </DetailKvTable>
                    </DetailPanel>
                )}

            {showEditVerification && (
                    <DetailPanel title={"검수관리"}>
                        <DetailKvTable>
                            <tbody>{editRows}</tbody>
                        </DetailKvTable>
                    </DetailPanel>
                )}
        </>
    );
}

export default MaintenanceVerification;
